/**
 * Hostel provider dashboard helpers (pure functions for API payload shaping).
 *
 * MySQL YEARWEEK(..., 1) integer: year * 100 + ISO week (1–53). We label as "YYYY-Www"
 * (ISO week number, zero-padded) for a stable, locale-neutral display without guessing
 * calendar boundaries incorrectly across environments.
 */

const NPR_FORMAT = new Intl.NumberFormat('en-NP', {
  style: 'currency',
  currency: 'NPR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Humanize bed status API keys for legend only (ONBORDED typo from backend). */
const STATUS_LABEL_OVERRIDES = {
  ONBORDED: 'Occupied',
  AVAILABLE: 'Available',
  BOOKED: 'Booked',
  HELD: 'Held',
};

/**
 * Parse API numeric strings or numbers; NaN-safe.
 * @param {unknown} value
 * @returns {number}
 */
export function parseNumericString(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

/**
 * @param {number} n
 * @returns {string}
 */
export function formatNpr(n) {
  return NPR_FORMAT.format(Number.isFinite(n) ? n : 0);
}

/**
 * @param {Record<string, number | string | undefined> | null | undefined} map
 * @returns {Array<{ key: string; count: number; label: string }>}
 */
export function bedStatusEntries(map) {
  if (!map || typeof map !== 'object') return [];
  return Object.keys(map).map((key) => ({
    key,
    count: Math.max(0, Math.round(parseNumericString(map[key]))),
    label: STATUS_LABEL_OVERRIDES[key] || key.replace(/_/g, ' '),
  }));
}

/**
 * @param {Record<string, number | string | undefined> | null | undefined} map
 * @returns {number}
 */
export function bedStatusTotal(map) {
  if (!map || typeof map !== 'object') return 0;
  return Object.keys(map).reduce((sum, k) => sum + Math.max(0, Math.round(parseNumericString(map[k]))), 0);
}

/**
 * @param {number | string | null | undefined} yearWeek — e.g. 202550
 * @returns {string}
 */
export function formatMysqlYearWeekLabel(yearWeek) {
  const n = parseNumericString(yearWeek);
  if (n <= 0) return '—';
  const y = Math.floor(n / 100);
  const w = n % 100;
  if (y < 1970 || y > 2100 || w < 1 || w > 53) return String(yearWeek);
  return `${y}-W${String(w).padStart(2, '0')}`;
}

/**
 * @param {string | null | undefined} from — YYYY-MM-DD
 * @param {string | null | undefined} to
 * @returns {string}
 */
export function formatFinanceRangeSubtitle(from, to) {
  if (!from || !to) return '';
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  if (!a || !b) return '';
  const sameMonth = a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  if (sameMonth) {
    return `${a.toLocaleDateString('en-NP', { month: 'short', day: 'numeric' })}–${b.getDate()}, ${b.getFullYear()}`;
  }
  return `${a.toLocaleDateString('en-NP', opts)} – ${b.toLocaleDateString('en-NP', opts)}`;
}

/**
 * @param {string} iso
 * @returns {Date | null}
 */
function parseIsoDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Normalize API dashboard payload for UI (tolerates missing keys).
 * @param {Record<string, unknown>} raw
 */
export function normalizeDashboardPayload(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const guests = r.guests && typeof r.guests === 'object' ? r.guests : {};

  const active = Math.max(
    0,
    Math.round(
      parseNumericString(guests.active ?? r.totalInventory)
    )
  );
  const onboardedThisMonth = Math.max(
    0,
    Math.round(
      parseNumericString(
        guests.onboardedThisMonth ?? r.totalOnboarded ?? r.activeOnboarded
      )
    )
  );

  const occupancyPct = parseNumericString(r.occupancyRate);

  let financeThisMonth = null;
  if (r.financeThisMonth != null && typeof r.financeThisMonth === 'object') {
    const f = r.financeThisMonth;
    financeThisMonth = {
      income: parseNumericString(f.income),
      expense: parseNumericString(f.expense),
      balance: parseNumericString(f.balance),
      rentIncome: parseNumericString(f.rentIncome),
      fromdate: f.fromdate != null ? String(f.fromdate) : '',
      todate: f.todate != null ? String(f.todate) : '',
    };
  }

  const bedStatusTotals =
    r.bedStatusTotals && typeof r.bedStatusTotals === 'object' ? { ...r.bedStatusTotals } : {};

  const spaces = Array.isArray(r.spaces) ? r.spaces : [];

  const weeklyGraph = Array.isArray(r.weeklyGraph)
    ? r.weeklyGraph.map((w) => ({
        week: parseNumericString(w.week),
        occupancyCount: Math.max(0, Math.round(parseNumericString(w.occupancyCount))),
        revenue: parseNumericString(w.revenue),
      }))
    : [];

  return {
    objectid: r.objectid != null ? String(r.objectid) : '',
    guests: { active, onboardedThisMonth },
    totalRevenue: String(r.totalRevenue ?? '0'),
    rentRevenueThisMonth: String(r.rentRevenueThisMonth ?? r.totalRevenue ?? '0'),
    occupancyRateDisplay: Number.isFinite(occupancyPct) ? occupancyPct : 0,
    financeThisMonth,
    bedStatusTotals,
    spaces,
    weeklyGraph,
  };
}

/** Distinct colors for dynamic bed status keys (cycles if many keys). */
export const BED_STATUS_COLORS = [
  '#10b981',
  '#6366f1',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#64748b',
];

/**
 * @param {string} key
 * @param {number} index
 * @returns {string}
 */
export function colorForStatusKey(key, index) {
  return BED_STATUS_COLORS[index % BED_STATUS_COLORS.length];
}

/**
 * @param {Array<{ occupancyCount: number; revenue: number }>} rows
 * @returns {{ maxCount: number; maxRevenue: number }}
 */
export function weeklyGraphMaxima(rows) {
  if (!rows.length) return { maxCount: 1, maxRevenue: 1 };
  const maxCount = Math.max(1, ...rows.map((r) => r.occupancyCount));
  const maxRevenue = Math.max(1, ...rows.map((r) => r.revenue));
  return { maxCount, maxRevenue };
}
